"use client";
import React, { useEffect, useRef, useState } from "react";
import styles from "./PerformanceMatrix.module.css";
import { aiChatBotPerformanceCategories as CATEGORIES } from "../../data/ai-chat-bot";

function Radial({ percent = 0, inView }) {
  const radius = 36;
  const circumference = 2 * Math.PI * radius;
  const dash = (percent / 100) * circumference;
  const offset = circumference - dash;

  return (
    <svg className={styles.radial} width="90" height="90" viewBox="0 0 90 90">
      <defs>
        <linearGradient id="g1" x1="0%" x2="100%">
          <stop offset="0%" stopColor="#ff9a3c" />
          <stop offset="100%" stopColor="#ff5b00" />
        </linearGradient>
      </defs>
      <g transform="translate(45,45)">
        <circle
          r={radius}
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="10"
        />
        <circle
          r={radius}
          fill="none"
          stroke="url(#g1)"
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={`${circumference} ${circumference}`}
          strokeDashoffset={inView ? offset : circumference}
          style={{
            transition: "stroke-dashoffset 900ms cubic-bezier(.2,.9,.2,1)",
          }}
        />
      </g>
    </svg>
  );
}

export default function PerformanceMatrix() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [startedAt, setStartedAt] = useState(null);
  const [activeHover, setActiveHover] = useState(null);

  // Controlled counters state for animated number roll
  const [counters, setCounters] = useState({});

  function animateNumber(id, target, opts = {}) {
    const duration = opts.duration ?? 700 + Math.random() * 300;
    const startFrom = Math.max(
      0,
      Math.floor(Math.random() * Math.min(10, Math.floor(target || 10)))
    );
    let raf;
    const start = performance.now();
    function tick(now) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      const current = startFrom + (target - startFrom) * eased;
      setCounters((s) => ({ ...s, [id]: current }));
      if (t < 1) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => {
      if (raf) cancelAnimationFrame(raf);
    };
  }

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            setStartedAt(Date.now());
            // Kick off per-metric counters with staggered delays
            CATEGORIES.forEach((cat, colIdx) => {
              cat.metrics.forEach((m, idx) => {
                const delayBase = colIdx * 180 + idx * 120 + 220;
                setTimeout(() => {
                  // animate numbers for percent/currency/count where numeric is available
                  if (typeof m.numeric === "number") {
                    animateNumber(m.id, m.numeric, {
                      duration: 700 + Math.random() * 300,
                    });
                  }
                }, delayBase);
              });
            });
            obs.disconnect();
          }
        });
      },
      { threshold: 0.2 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Equalize all metric card heights for a consistent grid
  useEffect(() => {
    const root = ref.current;
    if (!root) return;
    const cards = Array.from(root.querySelectorAll(`.${styles.card}`));
    if (!cards.length) return;
    function equalize() {
      // reset first to measure natural heights
      cards.forEach((c) => (c.style.minHeight = ""));
      let maxH = 0;
      cards.forEach((c) => {
        const h = c.getBoundingClientRect().height;
        if (h > maxH) maxH = h;
      });
      cards.forEach((c) => (c.style.minHeight = `${Math.ceil(maxH)}px`));
    }
    equalize();
    const ro = new ResizeObserver(() => equalize());
    cards.forEach((c) => ro.observe(c));
    window.addEventListener("resize", equalize);
    return () => {
      window.removeEventListener("resize", equalize);
      ro.disconnect();
    };
  }, [inView]);

  // Handlers for tilt effect
  function handleMouseMove(e, el) {
    const rect = el.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = e.clientX - cx;
    const dy = e.clientY - cy;
    const rx = (dy / rect.height) * 6;
    const ry = -(dx / rect.width) * 6;
    el.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(6px)`;
  }
  function handleMouseLeave(el) {
    el.style.transform = "";
  }

  return (
    <section
      className={styles.section}
      ref={ref}
      aria-labelledby="performance-matrix-title"
    >
      <div className="container">
        <div className="text-center mb-4" data-reveal="fade">
          <p className={styles.eyebrow}>AI Performance Matrix</p>
          <h2 id="performance-matrix-title" className={styles.sectionTitle}>
            Real-time impact across growth, efficiency and customer insight
          </h2>
          <p className={styles.sectionSub}>
            Benchmarks observed across production deployments. Results vary by
            business model, volume and integration depth.
          </p>
        </div>

        <div className={styles.grid}>
          {CATEGORIES.map((cat, colIdx) => (
            <div key={cat.id} className={styles.column}>
              <div className={styles.categoryPanel} data-reveal>
                <div className={styles.categoryHeader}>
                  <h3 className={styles.categoryTitle}>{cat.title}</h3>
                  <p className={styles.categorySubtitle}>{cat.subtitle}</p>
                </div>
                <div className={styles.cards}>
                  {cat.metrics.map((m, idx) => {
                    return (
                      <article
                        key={m.id}
                        className={`${styles.card} ${
                          activeHover && activeHover !== m.id
                            ? styles.dimmed
                            : ""
                        }`}
                        tabIndex={0}
                        aria-label={m.title}
                        onMouseEnter={(e) => {
                          setActiveHover(m.id);
                          const el = e.currentTarget;
                          el.classList.add(styles.cardHoverActive);
                        }}
                        onMouseMove={(e) => handleMouseMove(e, e.currentTarget)}
                        onMouseLeave={(e) => {
                          setActiveHover(null);
                          const el = e.currentTarget;
                          el.classList.remove(styles.cardHoverActive);
                          handleMouseLeave(el);
                        }}
                      >
                        <div className={styles.cardVizWrap}>
                          <div
                            className={`${styles.cardViz} ${
                              m.type === "percent" || m.type === "percent-neg"
                                ? styles.radialPulse
                                : ""
                            }`}
                          >
                            {m.type === "percent" ||
                            m.type === "percent-neg" ? (
                              <div className={styles.ringWrap}>
                                <Radial
                                  percent={inView ? m.numeric : 0}
                                  inView={inView}
                                />
                                <div className={styles.ringValue} aria-hidden>
                                  {inView ? m.value : ""}
                                </div>
                              </div>
                            ) : m.type === "multiplier" ? (
                              <div
                                className={`${styles.multiplier} ${
                                  inView ? styles.multiplierBounce : ""
                                }`}
                              >
                                {m.numeric}x
                              </div>
                            ) : m.type === "time" ? (
                              <div
                                className={`${styles.clock} ${
                                  inView ? styles.clockTick : ""
                                }`}
                                aria-hidden
                              >
                                <svg
                                  width="48"
                                  height="48"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                >
                                  <circle
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="url(#g)"
                                    strokeWidth="1.5"
                                    strokeOpacity="0.35"
                                  />
                                  <path
                                    d="M12 7v6l4 2"
                                    stroke="#ff9a3c"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              </div>
                            ) : m.type === "score" ? (
                              <div className={styles.score}>{m.value}</div>
                            ) : (
                              <div className={styles.count}>
                                {inView ? m.value : ""}
                              </div>
                            )}
                          </div>
                          <span
                            className={`${styles.typeChip} ${
                              styles[`chip-${m.type}`] || ""
                            }`}
                          >
                            {m.type === "percent" || m.type === "percent-neg"
                              ? "Rate"
                              : m.type === "multiplier"
                              ? "Multiplier"
                              : m.type === "time"
                              ? "Speed"
                              : m.type === "score"
                              ? "Score"
                              : m.type === "currency"
                              ? "Revenue"
                              : "Volume"}
                          </span>
                        </div>

                        <div className={styles.cardBody}>
                          <h4 className={styles.metricTitle}>
                            <span className={styles.metricAccent}>
                              {m.title}
                            </span>
                          </h4>
                          <div className={styles.metricValue}>
                            <span className={styles.bigValue}>
                              {m.type === "percent" || m.type === "percent-neg"
                                ? inView
                                  ? `${m.value}`
                                  : ""
                                : m.type === "currency"
                                ? inView
                                  ? counters[m.id]
                                    ? `$${counters[m.id].toFixed(2)}`
                                    : ""
                                  : ""
                                : m.type === "multiplier"
                                ? `${m.numeric}x`
                                : m.type === "count"
                                ? inView
                                  ? counters[m.id]
                                    ? `${Math.round(counters[m.id])}+`
                                    : ""
                                  : ""
                                : m.value}
                            </span>
                          </div>
                          <p className={styles.metricDesc}>{m.desc}</p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <p className={styles.footnote}>
          Indicative benchmarks shown for illustration. We co-define targets and
          measure actuals post-launch.
        </p>
      </div>
    </section>
  );
}
